import { CommonModule } from '@angular/common'
import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { Router, RouterModule } from '@angular/router'
import { ToastrService } from '../../../../../../services/toastr.service'
import { AuthService } from '../../../../../auth/services/auth.service'
import { ServerRepresentedEvent } from '../../../../../events/models/event.model'
import { EventService } from '../../../../../events/services/event.service'
import { PersonaTypes } from '../../../../../personas/enums/persona-types.enum'
import { Language } from '../../../../../personas/interfaces/persona-options.interface'
import { LanguageService } from '../../../../../personas/services/language.service'
import { PersonaService } from '../../../../../personas/services/persona.service'
import { AdminEventCardComponent } from '../../../shared/admin-event-card/admin-event-card.component'

interface GroupFormConfig {
  maximumEntries: number
  titlesMain: string
  addLabel: string
  allowImport: boolean
  allowInvitation: boolean
  groupFormTranslations: any[]
  selectedParent: string
  selectedChild: string
  parentPersonas: any[]
  childPersonas: any[]
  originalParentPersonas: any[]
  originalChildPersonas: any[]
  selectedEvent: ServerRepresentedEvent | null
  titlesAddlabels: string
  showGroupForms: boolean
  parentForms: any[]
}

interface CommonConfig {
  currentWorkspaceName: string
  currentWorkspaceSlug: string
  searchControl: FormControl
  availableLanguages: Language[]
}

interface EventsConfig {
  events: ServerRepresentedEvent[]
  loading: boolean
  noMoreEvents: boolean
  searchTerm: string
}

@Component({
  selector: 'ngx-group-forms',
  templateUrl: './group-forms.component.html',
  styleUrls: ['./group-forms.component.scss'],
  standalone: true,
  imports: [CommonModule, RouterModule, AdminEventCardComponent, ReactiveFormsModule, FormsModule],
  providers: [EventService]
})
export class GroupFormsComponent implements OnInit {
  @Input() embedded = false
  @Input() initialEvent: ServerRepresentedEvent | null = null
  @Output() exit = new EventEmitter<void>()

  private readonly _destroyRef = inject(DestroyRef)

  // Group Form properties
  public groupForm: GroupFormConfig = {
    maximumEntries: 5,
    titlesMain: '',
    addLabel: '',
    allowImport: true,
    allowInvitation: true,
    groupFormTranslations: [],
    selectedParent: '',
    selectedChild: '',
    parentPersonas: [],
    childPersonas: [],
    originalParentPersonas: [],
    originalChildPersonas: [],
    selectedEvent: null,
    titlesAddlabels: '',
    showGroupForms: false,
    parentForms: []
  }

  isUpdating: boolean = false

  // Common properties
  common: CommonConfig = {
    currentWorkspaceName: '',
    currentWorkspaceSlug: '',
    searchControl: new FormControl(''),
    availableLanguages: []
  }

  // Events list properties
  eventsConfig: EventsConfig = {
    events: [],
    loading: false,
    noMoreEvents: false,
    searchTerm: ''
  }

  currentParentForms: any

  constructor(
    private router: Router,
    private authService: AuthService,
    private toastr: ToastrService,
    private languageService: LanguageService,
    private eventService: EventService,
    private personaService: PersonaService
  ) {}

  ngOnInit(): void {
    // Check if user has access
    if (!this.authService.isAdmin && !this.authService.currentWorkspace) {
      this.router.navigate(['/workspaces/list'])
      return
    }

    // Set workspace information
    if (this.authService.currentWorkspace) {
      this.common.currentWorkspaceName = this.authService.currentWorkspace.name
      this.common.currentWorkspaceSlug = this.authService.currentWorkspace.slug
    }

    this._loadLanguages()
    if (this.embedded && this.initialEvent) {
      this.eventsConfig.events = [this.initialEvent]
      if (!this.hasExistingRelationships(this.initialEvent)) {
        this.viewEventDetails(this.initialEvent)
      }
      return
    }
    this.loadEvents()
  }

  /**
   * Loads available languages for translations
   */
  private _loadLanguages(): void {
    this.languageService
      .getLanguages()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((languages: Language[]) => {
        this.common.availableLanguages = languages
      })
  }

  /**
   * Clones the event forms array to avoid mutation of original data
   * Uses structuredClone for better performance than JSON.parse/stringify
   */
  private _cloneEventForms(forms: any[]): any[] {
    // Use structuredClone if available, fallback to JSON method
    if (typeof structuredClone !== 'undefined') {
      return structuredClone(forms)
    }
    return JSON.parse(JSON.stringify(forms))
  }

  /**
   * Filters forms to get valid parent/child personas
   * Excludes forms that have a parent or are of types GROUP, MEMBER, or GENERIC
   */
  private _filterValidPersonas(forms: any[]): any[] {
    return forms.filter((form) => form.parent === null && form.type !== PersonaTypes.GROUP && form.type !== PersonaTypes.MEMBER && form.type !== PersonaTypes.GENERIC)
  }

  /**
   * Initializes parent and child personas for form selection
   * Clones event forms and filters them to get valid personas
   */
  private _initializePersonas(event: ServerRepresentedEvent): void {
    const clonedForms = this._cloneEventForms(event.forms || [])
    let validPersonas = this._filterValidPersonas(clonedForms)

    if (this.isUpdating) {
      if (this.groupForm.selectedParent) {
        const selectedParentForm = clonedForms.find((form) => form.slug === this.groupForm.selectedParent)
        if (selectedParentForm && !validPersonas.find((persona) => persona.slug === selectedParentForm.slug)) {
          validPersonas.push(selectedParentForm)
        }
      }

      if (this.groupForm.selectedChild) {
        const selectedChildForm = clonedForms.find((form) => form.slug === this.groupForm.selectedChild)
        if (selectedChildForm && !validPersonas.find((persona) => persona.slug === selectedChildForm.slug)) validPersonas.push(selectedChildForm)
      }
    }

    this.groupForm.originalParentPersonas = validPersonas
    this.groupForm.parentPersonas = validPersonas
    this.groupForm.originalChildPersonas = validPersonas
    this.groupForm.childPersonas = validPersonas
  }

  /**
   * Loads events for the current workspace (upcoming and live only)
   */
  loadEvents(): void {
    if (this.eventsConfig.loading) return
    this.eventsConfig.loading = true

    // exceptStatus: 1 = exclude past events, so we get upcoming and live events only
    this.eventService
      .getEventsExcept(1, this.eventsConfig.searchTerm)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(
        (events) => {
          // Sort by status priority: Live first, then Upcoming, then by start date
          const sortedEvents = (events || []).sort((a, b) => {
            const statusA = this.getEventStatus(a)
            const statusB = this.getEventStatus(b)

            // Live events first
            if (statusA === 'Live' && statusB !== 'Live') return -1
            if (statusB === 'Live' && statusA !== 'Live') return 1

            // If both have same priority, sort by start date
            const dateA = new Date(a.public_start_date).getTime()
            const dateB = new Date(b.public_start_date).getTime()
            return dateA - dateB
          })

          this.eventsConfig.events = sortedEvents
          this.eventsConfig.noMoreEvents = true // getEventsExcept returns all events at once
          this.eventsConfig.loading = false
        },
        (error) => {
          console.error('Error loading events:', error)
          this.eventsConfig.loading = false
        }
      )
  }

  /**
   * Searches events based on search term
   */
  searchEvents(term): void {
    if (term?.length > 2) {
      this.eventsConfig.searchTerm = term || ''
      this.eventsConfig.events = []
      this.loadEvents()
    }
  }

  /**
   * Clears the search and reloads all events
   */
  clearSearch(): void {
    this.common.searchControl.setValue('')
    this.eventsConfig.searchTerm = ''
    this.eventsConfig.events = []
    this.loadEvents()
  }

  /**
   * Handles viewing event details and validates available forms for creating new relationships
   */
  viewEventDetails(event: ServerRepresentedEvent): void {
    if (!event.forms) {
      this.toastr.error('Error', 'Event has no forms')
      return
    }
    // Check for existing group relationships
    const existingGroupForms = event.forms.filter((form) => form.type === PersonaTypes.GROUP)

    // Check for valid forms that can be used to create new relationships
    const validForms = event.forms.filter((form) => form.type === PersonaTypes.VISITOR)

    // For creating new relationships, need at least 2 VISITOR forms
    if (validForms.length < 2) {
      this.toastr.error('Error', 'Event must have at least 2 available forms to create group relationships')
      return
    }

    // Go directly to create new relationship form (skip intermediate screen)
    this.startCreateNewRelationship()
    this.groupForm.selectedEvent = event
    this._initializePersonas(event)
  }

  /**
   * Updates event for group form editing
   */
  updateEvent(event: ServerRepresentedEvent): void {
    if (!event.forms) {
      this.toastr.error('Error', 'Event has no forms')
      return
    }

    const validForms = event.forms.filter((form) => form.type === PersonaTypes.GROUP)
    if (validForms.length < 1) {
      this.toastr.error('Error', 'Event has no group forms')
      return
    }
    this.isUpdating = true
    this.groupForm.showGroupForms = true
    this.groupForm.selectedEvent = event
    this._initializePersonas(event)
    this.currentParentForms = event.forms.filter((form) => form.type === PersonaTypes.GROUP)
  }

  /**
   * Edits a specific group relationship directly from event card
   */
  editSpecificRelationship(data: { event: ServerRepresentedEvent; relationship: any }): void {
    const { event, relationship } = data

    if (!event.forms) {
      this.toastr.error('Error', 'Event has no forms')
      return
    }

    // Set up editing mode with pre-selected parent and child forms
    this.isUpdating = true
    this.groupForm.showGroupForms = false // Don't show existing forms page
    this.groupForm.selectedEvent = event

    this.groupForm.selectedParent = relationship.parentSlug
    this.groupForm.selectedChild = relationship.childSlug

    // Set up the personas arrays
    this._initializePersonas(event)

    // Set current parent forms for editing
    this.currentParentForms = event.forms.filter((form) => form.type === PersonaTypes.GROUP)

    this._hydrateAllowFlagsFromParentForm()

    // Titles, maximumEntries, and translations come from the CHILD form (type 31)
    const childForm = event.forms.find((form) => form.slug === relationship.childSlug)
    if (childForm && childForm.options) {
      this.groupForm.maximumEntries = childForm.options.maximumEntries || 5
      this.groupForm.titlesMain = childForm.options.titles?.main || ''
      this.groupForm.addLabel = childForm.options.titles?.addLabel || ''

      // Parse translations from the correct structure
      if (childForm.options.titles?.translations && Array.isArray(childForm.options.titles.translations)) {
        this.groupForm.groupFormTranslations = []
        childForm.options.titles.translations.forEach((translation: any) => {
          const slug = Object.keys(translation)[0]
          this.groupForm.groupFormTranslations.push({
            slug: slug,
            titlesMain: translation[slug]?.main || '',
            titlesAddlabels: translation[slug]?.addLabel || ''
          })
        })
      }
    }
  }

  /**
   * Go back to event selection
   */
  backToEventSelection(): void {
    const embeddedEvent = this.embedded ? this.eventsConfig.events.find((event) => event.id === this.groupForm.selectedEvent?.id) : null
    const returnToCard = !!embeddedEvent && this.hasExistingRelationships(embeddedEvent)
    this.groupForm.selectedEvent = null
    this.groupForm.showGroupForms = false
    this.groupForm.selectedParent = ''
    this.groupForm.selectedChild = ''
    this.isUpdating = false
    this._resetFormSelections()
    if (this.embedded && !returnToCard) {
      this.exit.emit()
    }
  }

  /**
   * Cancel group form creation/editing
   */
  cancelGroupForm(): void {
    this.backToEventSelection()
  }

  /**
   * Handles parent form selection and updates available child forms
   */
  onParentChange(): void {
    this.groupForm.childPersonas = this.groupForm.originalChildPersonas.filter((p) => p.slug !== this.groupForm.selectedParent)
    this._hydrateAllowFlagsFromParentForm()
  }

  /**
   * Handles child form selection and updates available parent forms
   */
  onChildChange(): void {
    this.groupForm.parentPersonas = this.groupForm.originalParentPersonas.filter((p) => p.slug !== this.groupForm.selectedChild)
    this._hydrateAllowFlagsFromParentForm()
  }

  /**
   * Sets allowImport / allowInvitation from the selected parent form’s API options.
   */
  private _hydrateAllowFlagsFromParentForm(): void {
    const event = this.groupForm.selectedEvent
    if (!event?.forms?.length || !this.groupForm.selectedParent) {
      return
    }
    const parentForm = event.forms.find((form) => form.slug === this.groupForm.selectedParent)
    if (!parentForm?.options) {
      return
    }
    this.groupForm.allowImport = parentForm.options.allowImport ?? true
    this.groupForm.allowInvitation = parentForm.options.allowInvitation ?? true
  }

  /**
   * Track by function for translation list
   */
  trackByTranslation(index: number, translation: any): number {
    return index
  }

  /**
   * Adds a new translation row to the form
   */
  addNewTranslation(): void {
    this.groupForm.groupFormTranslations.push({
      slug: '',
      titlesMain: '',
      titlesAddlabels: ''
    })
  }

  /**
   * Removes a translation row from the form
   */
  removeTranslation(index: number): void {
    this.groupForm.groupFormTranslations.splice(index, 1)
  }

  /**
   * Gets child persona for a parent persona
   */
  getChildPersona(parentPersonas: any): any {
    return this.groupForm.selectedEvent?.forms?.find((form) => form.parent == parentPersonas.id)
  }

  /**
   * Handles persona selection
   */
  personaSelected(form: any, childPersona: any): void {
    const titles = childPersona.options.titles
    this.groupForm.selectedParent = form.slug
    this.groupForm.selectedChild = childPersona.slug
    this.groupForm.titlesMain = titles.main || ''
    this.groupForm.addLabel = titles.addLabel || ''
    this.groupForm.maximumEntries = childPersona.options.maximumEntries || 5
    childPersona.options.titles.translations.forEach((translation: any) => {
      const slug = Object.keys(translation)[0]
      this.groupForm.groupFormTranslations.push({
        slug: slug,
        titlesMain: translation[slug].main,
        titlesAddlabels: translation[slug].addLabel
      })
    })
    this.groupForm.showGroupForms = false
  }

  /**
   * Submits the group form with all collected data
   */
  submitGroupForm(): void {
    if (!this.groupForm.selectedEvent || !this.groupForm.selectedParent || !this.groupForm.selectedChild) {
      this.toastr.error('Error', 'Please select an event, parent and child forms')
      return
    }

    const payload = {
      workspace_slug: this.common.currentWorkspaceSlug,
      event_slug: this.groupForm.selectedEvent.slug,
      main_form_slug: this.groupForm.selectedParent,
      sub_form_slug: this.groupForm.selectedChild,
      maximumEntries: this.groupForm.maximumEntries,
      titles_main: this.groupForm.titlesMain,
      addLabel: this.groupForm.addLabel,
      allowImport: !!this.groupForm.allowImport,
      allowInvitation: !!this.groupForm.allowInvitation,
      translations: this.groupForm.groupFormTranslations.map((t) => ({
        lang: t.slug,
        lang_titles_main: t.titlesMain,
        lang_addLabel: t.titlesAddlabels
      }))
    }

    this.personaService
      .createGroupForm(payload)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          const savedEventId = this.groupForm.selectedEvent?.id
          this.toastr.success('Success', this.isUpdating ? 'Group form updated successfully' : 'Group form created successfully')

          // Reset form state
          this._resetGroupForm()
          this.isUpdating = false

          if (this.embedded && savedEventId) {
            this._reloadEmbeddedEvent(savedEventId)
            return
          }

          // Reload events to get fresh data from the server
          this.eventsConfig.events = []
          this.loadEvents()
        },
        error: (error) => {
          console.error('Error creating/updating group form:', error)
          this.toastr.error('Error', this.isUpdating ? 'Failed to update group form' : 'Failed to create group form')
        }
      })
  }

  /**
   * Refreshes the open event so its card lists the relationship that was just saved.
   */
  private _reloadEmbeddedEvent(eventId: number): void {
    const current = this.eventsConfig.events.find((event) => event.id === eventId) || this.initialEvent
    if (!current) return

    this.eventsConfig.loading = true
    this.eventService
      .getEventForms(eventId)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (forms) => {
          if (current) {
            this.eventsConfig.events = [{ ...current, forms: forms || current.forms || [] }]
          }
          this.eventsConfig.loading = false
        },
        error: () => {
          if (current) this.eventsConfig.events = [current]
          this.eventsConfig.loading = false
        }
      })
  }

  /**
   * Resets all group form fields to their default values
   */
  private _resetGroupForm(): void {
    this.groupForm.selectedParent = ''
    this.groupForm.selectedChild = ''
    this.groupForm.titlesMain = ''
    this.groupForm.addLabel = ''
    this.groupForm.allowImport = true
    this.groupForm.allowInvitation = true
    this.groupForm.groupFormTranslations = []
    this.groupForm.maximumEntries = 5
    this.groupForm.selectedEvent = null
    this.isUpdating = false
  }

  /**
   * Get event status based on current date vs event dates
   */
  getEventStatus(event: any): string {
    if (!event || !event.public_start_date || !event.public_end_date) {
      return 'Unknown'
    }

    const now = new Date()
    const startDate = new Date(event.public_start_date)
    const endDate = new Date(event.public_end_date)

    if (now > endDate) {
      return 'Finished' // Event has ended
    } else if (now >= startDate && now <= endDate) {
      return 'Live' // Event is currently running
    } else if (now < startDate) {
      return 'Upcoming' // Event hasn't started yet
    } else {
      return 'Unknown'
    }
  }

  /**
   * Get CSS class for event status
   */
  getEventStatusClass(event: any): string {
    const status = this.getEventStatus(event)

    switch (status) {
      case 'Finished':
        return 'status-completed'
      case 'Live':
        return 'status-live'
      case 'Upcoming':
        return 'status-published'
      default:
        return 'status-unknown'
    }
  }

  /**
   * Gets the count of existing group form relationships for an event
   */
  getExistingGroupFormsCount(event: ServerRepresentedEvent): number {
    if (!event || !event.forms) {
      return 0
    }
    return event.forms.filter((form) => form.type === PersonaTypes.GROUP).length
  }

  /**
   * Gets the count of available forms that can be used for new relationships
   */
  getAvailableFormsCount(event: ServerRepresentedEvent): number {
    if (!event || !event.forms) {
      return 0
    }
    return event.forms.filter((form) => form.type === PersonaTypes.VISITOR).length
  }

  /**
   * Checks if the event can create new group relationships
   */
  canCreateNewRelationship(event: ServerRepresentedEvent): boolean {
    return this.getAvailableFormsCount(event) >= 2
  }

  /**
   * Checks if the event has existing group relationships
   */
  hasExistingRelationships(event: ServerRepresentedEvent): boolean {
    return this.getExistingGroupFormsCount(event) > 0
  }

  /**
   * Starts the process of creating a new group relationship
   */
  startCreateNewRelationship(): void {
    this.isUpdating = false
    this.groupForm.showGroupForms = false // Go directly to form editing, not existing forms page
    this._resetFormSelections()
  }

  /**
   * Resets form selections for new relationship creation
   */
  private _resetFormSelections(): void {
    this.groupForm.selectedParent = ''
    this.groupForm.selectedChild = ''
    this.groupForm.maximumEntries = 5
    this.groupForm.titlesMain = ''
    this.groupForm.addLabel = ''
    this.groupForm.allowImport = true
    this.groupForm.allowInvitation = true
    this.groupForm.groupFormTranslations = []
  }
}
